import { Modal } from '@/components/admin/Modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { ResponsePromptImportResponse } from '@innermate/shared';
import { AlertCircle, CheckCircle2, FileText, Folder, GitMerge } from 'lucide-react';

interface Props {
  open: boolean;
  onClose: () => void;
  /** null = "가져오기 실패" 모드 (서버 응답 자체가 실패). 메시지는 fatalError 에. */
  result: ResponsePromptImportResponse | null;
  fatalError?: string | null;
}

/**
 * Import 결과 — alert 대신 모달. 추가/건너뜀 카운트 요약 + 개별 에러 리스트.
 * 에러 항목은 type 아이콘 + name + (file 이면 slug) + reason 으로 사람이 식별 가능.
 */
export function ImportResultModal({ open, onClose, result, fatalError }: Props) {
  const ok = !fatalError && result != null;
  const hasErrors = !!result && result.errors.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={fatalError ? '가져오기 실패' : '가져오기 완료'}
      description={
        fatalError
          ? '서버 응답을 받지 못했거나 파일을 읽을 수 없습니다.'
          : '추가된 항목과 건너뛴 항목을 확인하세요.'
      }
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button type="button" onClick={onClose}>
            닫기
          </Button>
        </div>
      }
    >
      {fatalError ? (
        <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {fatalError}
        </div>
      ) : (
        <div className="space-y-3">
          {ok ? (
            <div className="flex flex-wrap gap-4 rounded-md border border-border bg-surface-muted/30 px-4 py-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-5 text-emerald-600" />
                <div>
                  <div className="text-xs text-foreground-muted">추가</div>
                  <div className="text-lg font-semibold">{result.imported}</div>
                </div>
              </div>
              <div className="border-l border-border" />
              <div
                className="flex items-center gap-2"
                title="folder 는 이름 일치 시 재사용, file 은 slug 일치 시 새 version 추가"
              >
                <GitMerge className="size-5 text-sky-600" />
                <div>
                  <div className="text-xs text-foreground-muted">병합</div>
                  <div className="text-lg font-semibold">{result.merged}</div>
                </div>
              </div>
              <div className="border-l border-border" />
              <div className="flex items-center gap-2">
                <AlertCircle className="size-5 text-foreground-muted" />
                <div>
                  <div className="text-xs text-foreground-muted">건너뜀</div>
                  <div className="text-lg font-semibold">{result.skipped}</div>
                </div>
              </div>
            </div>
          ) : null}

          {ok && result.merged > 0 ? (
            <div className="rounded-md bg-sky-500/10 px-3 py-2 text-xs text-sky-700 dark:text-sky-400">
              병합된 file 은 <strong>slug 가 같은 기존 file</strong> 의 위치에 새 version 으로
              추가됐습니다. 만약 import JSON 의 경로와 다른 폴더라면 결과 트리에서 위치를
              확인하세요.
            </div>
          ) : null}

          {hasErrors ? (
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs text-foreground-muted">
                건너뛴 항목
                <Badge variant="muted">{result.errors.length}</Badge>
              </div>
              <ul className="divide-y divide-border rounded-md border border-border">
                {result.errors.map((er) => (
                  <li
                    key={`${er.tempId}-${er.reason}`}
                    className="flex items-start gap-2 px-3 py-2 text-sm"
                  >
                    {er.type === 'folder' ? (
                      <Folder className="size-4 mt-0.5 shrink-0 text-foreground-muted" />
                    ) : (
                      <FileText className="size-4 mt-0.5 shrink-0 text-foreground-muted" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-medium truncate">{er.name}</span>
                        {er.slug ? (
                          <code className="text-[10px] text-foreground-muted font-mono">
                            {er.slug}
                          </code>
                        ) : null}
                      </div>
                      <div className="text-xs text-destructive mt-0.5">{er.reason}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : ok && (result.imported > 0 || result.merged > 0) ? (
            <div className="rounded-md bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
              모든 항목이 정상적으로 처리되었습니다.
              {result.merged > 0 ? (
                <span className="ml-1 text-foreground-muted">
                  (병합 {result.merged} 건 — 기존 항목에 새 version/variant 추가)
                </span>
              ) : null}
            </div>
          ) : ok ? (
            <div className="rounded-md bg-surface-muted/50 px-3 py-2 text-sm text-foreground-muted">
              가져온 항목이 없습니다.
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
